<template>
    <div class="mt-6" v-if="fields?.length">
        <div>
            <vx-label> Fields </vx-label>
        </div>

        <div class="flex flex-col gap-4">
            <snippet-template-form-field
                v-for="(field, index) in fields"
                :key="field.key"
                v-model="fields[index] as SnippetField"
                @remove="removeField(field)"
                :disabled="props.disabled"
            >
            </snippet-template-form-field>
        </div>
    </div>

    <vx-modal v-model:visible="addingModal" padding="standard" size="sm">
        <template v-slot:header> {{ maxReached ? 'Existing' : 'New' }} Field </template>
        <vx-alert v-if="maxReached" class="mb-3" :closeable="false">
            Maximum number of fields reached. Please reuse existing fields.
        </vx-alert>

        <vx-label size="md" class="mb-3">
            Name
            <template v-slot:description>
                Choose a name for this field. This name will help users understand what information to enter. Field
                names are locked after creation.
            </template>
        </vx-label>

        <vx-selectable
            v-if="fields.length > 0"
            v-model="newFieldName"
            :taggable="!maxReached"
            :error="displayFirstError(validation.name)"
            :options="fields"
            :reduce="(option: SnippetField) => option.name"
            label="name"
            :clearable="false"
            size="lg"
            relative-dropdown
        >
            <template v-slot:no-options>
                Maximum number of fields reached. Please reuse existing fields.
            </template>
        </vx-selectable>
        <vx-input v-else v-model="newFieldName" :error="displayFirstError(validation.name)" size="lg"></vx-input>

        <div class="text-xs">
            <font-awesome-icon :icon="faCircleInfo" />
            Max length of 40 characters
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="addingModal = false"
                    tabindex="1"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button @click="addField()" type="button" size="lg" block color="primary">
                    Add Field
                    <font-awesome-icon :icon="faCirclePlus" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script lang="ts" setup>
import { faCircleInfo, faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf, helpers } from '@vuelidate/validators';
import { displayFirstError, focusInvalidField } from '~/utils/validation';
import type { SnippetField } from '~/services/snippets/snippetsClient.types';
import { useToasted } from '~/composables/useToasted';
import SnippetTemplateFormField from './SnippetTemplateFormField.vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ref, computed } from 'vue';
import { VxLabel, VxInput, VxModal, VxButton, VxAlert, VxSelectable } from '@voxie/frontend-components';
import type { PropType } from 'vue';

const MAX_SNIPPET_FIELDS = 10;

const generateFieldKey = (name: string) => {
    return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s_-]/g, '')
        .trim()
        .replace(/[\s-]+/g, '_')
        .replace(/^[^a-z]+/, '')
        .replace(/_+$/, '');
};

const snippetFieldKey = helpers.withMessage(
    'Field name contains invalid characters that prevent generating a valid key',
    (value?: string) => {
        if (!value) {
            return true;
        }

        const key = generateFieldKey(value);
        return '' !== key;
    }
);

const toasted = useToasted();
const props = defineProps({
    body: { type: String, required: true },
    disabled: Boolean
});
const emit = defineEmits<{
    added: [field: SnippetField];
}>();

const addingModal = defineModel('adding', {
    type: Boolean,
    required: true,
});

const fields = defineModel({
    type: Object as PropType<SnippetField[]>,
    required: true,
    default: () => [],
});

const newFieldName = ref('');
const validation = useVuelidate(
    {
        name: {
            // necessary because the model is nested inside a form
            required: requiredIf(() => addingModal.value),
            maxLength: maxLength(40),
            validKey: snippetFieldKey,
        },
    },
    computed(() => {
        return {
            name: newFieldName.value,
        };
    })
);
const addField = async () => {
    const isValid = await validation.value.$validate();

    if (!isValid) {
        focusInvalidField();
        return;
    }

    const generatedKey = generateFieldKey(newFieldName.value);
    const existingField = fields.value.find((field) => field.key === generatedKey);

    if (!existingField) {
        const newField: SnippetField = {
            name: newFieldName.value,
            description: null,
            key: generatedKey,
            is_required: true,
            min_length: null,
            max_length: null,
        };
        fields.value.push(newField);
        emit('added', newField);
    } else {
        emit('added', existingField);
    }

    newFieldName.value = '';
    validation.value.$reset();
    addingModal.value = false;
};

const removeField = async (fieldToRemove: SnippetField) => {
    if (props.body.includes(fieldToRemove.key)) {
        toasted.error('First remove the field from the message');
        return;
    }

    fields.value = fields.value.filter((field) => field.key !== fieldToRemove.key);
};

const maxReached = computed(() => fields.value.length >= MAX_SNIPPET_FIELDS);
</script>
